/**
 * Driver onboarding hook — mirrors `useAdminOnboarding` but scoped to
 * `role === "driver"` and uses its own localStorage flag so the admin
 * tour (if a user is both) is tracked independently.
 */

import { useCallback, useEffect, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { driverSteps } from "./driverTourSteps";

const STORAGE_PREFIX = "sdrive_driver_onboarding_seen_v1:";

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || "anonymous"}`;
}

function resolvableSteps(steps) {
  if (typeof document === "undefined") return steps;
  return steps.filter((s) => {
    if (!s.element) return true;
    try {
      return !!document.querySelector(s.element);
    } catch {
      return false;
    }
  });
}

export function useDriverOnboarding(user) {
  const driverRef = useRef(null);

  const start = useCallback(() => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* no-op */ }
      driverRef.current = null;
    }
    const steps = resolvableSteps(driverSteps);
    if (!steps.length) return;

    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 4,
      stageRadius: 8,
      smoothScroll: true,
      popoverClass: "sdrive-onboarding-popover",
      progressText: "{{current}} / {{total}}",
      nextBtnText: "Suivant →",
      prevBtnText: "← Précédent",
      doneBtnText: "Terminer",
      onDestroyed: () => {
        if (user?.id) {
          try { localStorage.setItem(storageKey(user.id), "1"); } catch { /* ignore */ }
        }
      },
      steps,
    });
    driverRef.current = d;
    d.drive();
  }, [user?.id]);

  const reset = useCallback(() => {
    if (!user?.id) return;
    try { localStorage.removeItem(storageKey(user.id)); } catch { /* ignore */ }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (user.role !== "driver") return;
    let seen = "0";
    try { seen = localStorage.getItem(storageKey(user.id)) || "0"; } catch { /* ignore */ }
    if (seen === "1") return;
    const t = setTimeout(() => start(), 650);
    return () => clearTimeout(t);
  }, [user?.id, user?.role, start]);

  useEffect(() => () => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* no-op */ }
      driverRef.current = null;
    }
  }, []);

  return { start, reset };
}
